import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import QRCode from 'qrcode';
import { qrMenuService, QrMenuStatus } from '@/data/qrMenuService';
import { downloadQrPosterPdf, downloadMenuPdf } from '@/utils/qrMenuPdf';
import { useUserViewMode } from '@/hooks/useUserViewMode';

interface Props {
  branchId: number;
  branchName?: string;
  companyName?: string;
}

type ConfirmAction = 'regenerate' | 'disable' | null;

const BranchQrMenuManager = ({ branchId, branchName, companyName }: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { isSuperAdmin, isCompanyAdmin, isBranchAdmin } = useUserViewMode();
  const canManage = isSuperAdmin || isCompanyAdmin || isBranchAdmin;

  const [status, setStatus] = useState<QrMenuStatus | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const loadStatus = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await qrMenuService.getStatus(branchId);
      setStatus(result);
    } catch (err) {
      console.error('Error loading QR menu status:', err);
      setError(t('qr.menu.load.error'));
    } finally {
      setLoading(false);
    }
  }, [branchId, t]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    let cancelled = false;
    if (status?.url) {
      QRCode.toDataURL(status.url, { width: 220, margin: 1 })
        .then((dataUrl) => {
          if (!cancelled) setQrDataUrl(dataUrl);
        })
        .catch((err) => console.error('Error rendering QR preview:', err));
    } else {
      setQrDataUrl('');
    }
    return () => {
      cancelled = true;
    };
  }, [status?.url]);

  const handleGenerate = async (regenerate = false) => {
    setActionLoading(true);
    try {
      const result = await qrMenuService.generate(branchId, regenerate);
      setStatus(result);
      enqueueSnackbar(t('qr.menu.generated'), { variant: 'success' });
    } catch (err) {
      console.error('Error generating QR menu:', err);
      enqueueSnackbar(t('qr.menu.error'), { variant: 'error' });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const handleDisable = async () => {
    setActionLoading(true);
    try {
      await qrMenuService.disable(branchId);
      setStatus((prev) =>
        prev ? { ...prev, enabled: false, token: null, url: null } : prev
      );
      enqueueSnackbar(t('qr.menu.disabled.success'), { variant: 'success' });
    } catch (err) {
      console.error('Error disabling QR menu:', err);
      enqueueSnackbar(t('qr.menu.error'), { variant: 'error' });
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  const handleCopyUrl = async () => {
    if (!status?.url) return;
    try {
      await navigator.clipboard.writeText(status.url);
      enqueueSnackbar(t('qr.menu.copied'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('qr.menu.error'), { variant: 'error' });
    }
  };

  const handleDownloadQrPdf = async () => {
    if (!status?.url) return;
    setActionLoading(true);
    try {
      await downloadQrPosterPdf({
        companyName,
        branchName: branchName || status.branchName,
        url: status.url
      });
    } catch (err) {
      console.error('Error downloading QR PDF:', err);
      enqueueSnackbar(t('qr.menu.error'), { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadMenuPdf = async () => {
    if (!status?.token) return;
    setActionLoading(true);
    try {
      const data = await qrMenuService.getMenuData(status.token);
      await downloadMenuPdf(data);
    } catch (err) {
      console.error('Error downloading menu PDF:', err);
      enqueueSnackbar(t('qr.menu.error'), { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={1}>
        <QrCode2Icon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h5">{t('qr.menu')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {t('qr.menu.description')}
      </Typography>
      <Divider sx={{ my: 2 }} />

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadStatus}>
              {t('qr.menu.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      ) : !status?.enabled ? (
        <Stack spacing={2} alignItems="flex-start">
          <Alert severity="info" sx={{ width: '100%' }}>
            {t('qr.menu.not.generated.info')}
          </Alert>
          <Button
            variant="contained"
            startIcon={<QrCode2Icon />}
            disabled={actionLoading || !canManage}
            onClick={() => handleGenerate(false)}
          >
            {t('qr.menu.generate')}
          </Button>
        </Stack>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} sm="auto">
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'background.paper'
              }}
            >
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={t('qr.menu')}
                  style={{ width: 180, height: 180 }}
                />
              ) : (
                <CircularProgress size={28} />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('qr.menu.url')}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  <TextField
                    value={status.url || ''}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <Tooltip title={t('qr.menu.copy')}>
                    <IconButton onClick={handleCopyUrl} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('qr.menu.open')}>
                    <IconButton
                      component="a"
                      href={status.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={1}>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  disabled={actionLoading}
                  onClick={handleDownloadQrPdf}
                >
                  {t('qr.menu.download.qr.pdf')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RestaurantMenuIcon />}
                  disabled={actionLoading}
                  onClick={handleDownloadMenuPdf}
                >
                  {t('qr.menu.download.menu.pdf')}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<RefreshIcon />}
                  disabled={actionLoading || !canManage}
                  onClick={() => setConfirmAction('regenerate')}
                >
                  {t('qr.menu.regenerate')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={actionLoading || !canManage}
                  onClick={() => setConfirmAction('disable')}
                >
                  {t('qr.menu.disable')}
                </Button>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      )}

      <Dialog
        open={confirmAction !== null}
        onClose={() => (actionLoading ? undefined : setConfirmAction(null))}
      >
        <DialogTitle>
          {confirmAction === 'regenerate'
            ? t('qr.menu.regenerate')
            : t('qr.menu.disable')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === 'regenerate'
              ? t('qr.menu.regenerate.confirm')
              : t('qr.menu.disable.confirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmAction(null)}
            disabled={actionLoading}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            color={confirmAction === 'regenerate' ? 'warning' : 'error'}
            disabled={actionLoading}
            onClick={() =>
              confirmAction === 'regenerate'
                ? handleGenerate(true)
                : handleDisable()
            }
          >
            {confirmAction === 'regenerate'
              ? t('qr.menu.regenerate')
              : t('qr.menu.disable')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchQrMenuManager;
